import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';
import { node } from 'prop-types';

// Promise-based replacement for window.confirm / window.prompt.
//
// Usage:
//   const { confirm, prompt } = useConfirm();
//   if (!(await confirm('Delete this item?'))) return;
//   const url = await prompt('Enter URL:');   // null if cancelled
//
// The provider is mounted once at the app root (gatsby wrapRootElement), so the
// dialog renders above everything and a single instance serves the whole app.

const SBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
`;

const SCard = styled.div`
    background: #fff;
    border-radius: 10px;
    max-width: 440px;
    width: 92%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SMessage = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #182a57;
    white-space: pre-wrap;
`;

const SInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid #d0d4dc;
    border-radius: 6px;
    font-size: 0.9rem;

    &:focus {
        outline: none;
        border-color: #f53103;
    }
`;

const SActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const SButton = styled.button`
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid transparent;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    ${({ $variant }) =>
        $variant === 'primary'
            ? `
        background: #f53103;
        color: #fff;
        &:hover { background: #c42702; }
    `
            : `
        background: #fff;
        color: #555;
        border-color: #d0d4dc;
        &:hover { background: #f5f5f7; }
    `}
`;

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
    // `null` when closed; otherwise the active dialog descriptor.
    const [dialog, setDialog] = useState(null);
    const [inputValue, setInputValue] = useState('');
    // Resolver for the in-flight promise.
    const resolveRef = useRef(null);

    const close = useCallback((result) => {
        setDialog(null);
        setInputValue('');
        if (resolveRef.current) {
            resolveRef.current(result);
            resolveRef.current = null;
        }
    }, []);

    const confirm = useCallback(
        (message, options = {}) =>
            new Promise((resolve) => {
                resolveRef.current = resolve;
                setDialog({
                    type: 'confirm',
                    message,
                    confirmLabel: options.confirmLabel || 'Confirm',
                    cancelLabel: options.cancelLabel || 'Cancel',
                });
            }),
        [],
    );

    const promptInput = useCallback(
        (message, defaultValue = '') =>
            new Promise((resolve) => {
                resolveRef.current = resolve;
                setInputValue(defaultValue);
                setDialog({
                    type: 'prompt',
                    message,
                    confirmLabel: 'OK',
                    cancelLabel: 'Cancel',
                });
            }),
        [],
    );

    const value = useMemo(
        () => ({ confirm, prompt: promptInput }),
        [confirm, promptInput],
    );

    const isPrompt = dialog?.type === 'prompt';

    const handleAccept = useCallback(() => {
        close(isPrompt ? inputValue : true);
    }, [close, isPrompt, inputValue]);

    const handleCancel = useCallback(() => {
        close(isPrompt ? null : false);
    }, [close, isPrompt]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            {dialog && (
                <SBackdrop
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) handleCancel();
                    }}
                >
                    <SCard
                        role="dialog"
                        aria-modal="true"
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') handleCancel();
                            if (e.key === 'Enter' && isPrompt) handleAccept();
                        }}
                    >
                        <SMessage>{dialog.message}</SMessage>
                        {isPrompt && (
                            <SInput
                                value={inputValue}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus
                                onChange={(e) => setInputValue(e.target.value)}
                            />
                        )}
                        <SActions>
                            <SButton type="button" onClick={handleCancel}>
                                {dialog.cancelLabel}
                            </SButton>
                            <SButton
                                type="button"
                                $variant="primary"
                                onClick={handleAccept}
                            >
                                {dialog.confirmLabel}
                            </SButton>
                        </SActions>
                    </SCard>
                </SBackdrop>
            )}
        </ConfirmContext.Provider>
    );
};

ConfirmProvider.propTypes = {
    children: node,
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return ctx;
};
